import React, { useEffect, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import CopyToClipboard from "react-copy-to-clipboard";
import { removeAllTags } from "../../../../config/utils";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";

const AnalyseBand = ({
    mainFields,
    restFields,
    band,
    index,
    setShowRichTextPopup,
    bandKey,
    isInaccessible,
    isDefaultOpen,
    isAiAnalyse = false,
    keys = "",
    onReferenceFieldClick,
    scrollContainerRef,
    containerKey,
    hasAssetValue = false,
    onAssetValueSave,
    isMobileRecommendation = false,
    assetValue = {},
    saveDisabledReason = "",
    onEditValueClick
}) => {
    const bandRef = useRef(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isOpen, setIsOpen] = useState(isDefaultOpen ?? false);
    const hasOpenedOnce = useRef(false);
    useEffect(() => {
        if (isAiAnalyse && isOpen) {
            if (!hasOpenedOnce.current && isDefaultOpen) {
                if (isOpen) {
                    hasOpenedOnce.current = true;
                }
                return;
            }
            const collapseEl = document.getElementById(keys ? `collapse-${keys}-${index + 1}` : `collapse-${index + 1}`);
            if (!collapseEl) return;

            const handleScroll = () => {
                const container = scrollContainerRef?.current?.[containerKey];
                const band = bandRef?.current;
                if (band && container && isAiAnalyse) {
                    const containerRect = container?.getBoundingClientRect();
                    const bandRect = band?.getBoundingClientRect();
                    const scrollTop = container?.scrollTop;
                    const offset = bandRect?.top - containerRect?.top;
                    container.scrollTo({
                        top: scrollTop + offset,
                        behavior: "smooth"
                    });
                }
            };

            const timer = setTimeout(handleScroll, 300);

            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const toggleAccordion = () => setIsOpen(!isOpen);
    const handleCopy = value => {
        setCopySuccess(value);
        setTimeout(() => {
            setCopySuccess(false);
        }, 3500);
    };

    const isKeyValueObject = value => value && typeof value === "object" && !Array.isArray(value) && Object.keys(value)?.length > 0;
    
    const isArrayOfKeyValueObjects = value => Array.isArray(value) && value?.length > 0 && value?.every(isKeyValueObject);

    const getDisplayValue = value => {
        if (value === null || value === undefined || value === "") return "";
        if (typeof value === "object") return JSON.stringify(value);
        return value?.toString();
    };
    const getArrayValues = value => {
        if (!Array.isArray(value)) return [];
        if (isArrayOfKeyValueObjects(value)) return value;

        return value
            ?.flatMap(item => (isKeyValueObject(item) ? Object.values(item)?.map(getDisplayValue) : [getDisplayValue(item)]))
            ?.filter(Boolean);
    };

    const getParsedArrayValue = value => {
        if (typeof value !== "string") return null;

        const trimmedValue = value.trim();
        if (!trimmedValue.startsWith("[") || !trimmedValue.endsWith("]")) return null;

        try {
            const parsedValue = JSON.parse(trimmedValue);
            return Array.isArray(parsedValue) ? parsedValue : null;
        } catch (error) {
            return null;
        }
    };

    const getStringArrayValues = value => {
        if (typeof value !== "string") return [];

        const trimmedValue = value.trim();
        if (!trimmedValue.startsWith("[") || !trimmedValue.endsWith("]")) return [];

        const parsedValue = getParsedArrayValue(trimmedValue);
        if (parsedValue) return getArrayValues(parsedValue);

        const innerValue = trimmedValue.slice(1, -1)?.trim();
        if (!innerValue) return [];

        const separator = innerValue.includes(";") ? ";" : ",";
        const values = innerValue
            ?.split(separator)
            ?.map(item => item?.trim())
            ?.filter(Boolean);
        if (!values?.length) return [];

        return values;
    };

    const getReferenceFieldValues = value => (Array.isArray(value) ? getArrayValues(value) : getStringArrayValues(value));
    const shouldUseReferenceTable = value => {
        if (Array.isArray(value)) return isArrayOfKeyValueObjects(value);

        const parsedValue = getParsedArrayValue(value);
        return isArrayOfKeyValueObjects(parsedValue);
    };
    const hasReferenceFieldValues = value => getReferenceFieldValues(value)?.length > 0;
    const getReferenceFieldCount = value => {
        const referenceValues = getReferenceFieldValues(value);
        return referenceValues?.length || 0;
    };
    const isEllipsisAiValue = fieldName => isAiAnalyse && fieldName !== "Recommendation" && fieldName !== "Narrative";
    const getFieldSaveDisabledReason = fieldName => {
        if (saveDisabledReason) return saveDisabledReason;
        if (fieldName === "Useful_Service_Life" || fieldName === "installed_year") return "Field cannot be saved";

        return "";
    };

    const divCounts = bandKey === "survey_details" ? 3 : 4;
    const outerDivCount = Math.ceil(Object.keys(restFields ?? {})?.length / divCounts);
    const copyFunction = eachRestField =>
        isAiAnalyse && !Array.isArray(restFields?.[eachRestField]?.value) && !hasReferenceFieldValues(restFields?.[eachRestField]?.value) ? (
            !restFields?.[eachRestField]?.value ? (
                <div className="icon ai-copy cursor-diabled">
                    <i className="fa fa-copy" data-tip="No value to copy" data-for="Asset_details" data-place="left" style={{ color: "#4B90FA" }} />
                </div>
            ) : (
                <CopyToClipboard
                    text={restFields?.[eachRestField]?.value?.toString()?.replace(/\s*\(\s*(?:image|web)(?:\s*\|\s*(?:image|web))?\s*\)/gi, "")}
                    onCopy={() => handleCopy(restFields?.[eachRestField]?.label)}
                >
                    <div className="icon cursor-hand ai-copy">
                        {copySuccess === restFields?.[eachRestField]?.label ? (
                            <i
                                className="fa fa-check"
                                style={{ color: "green" }}
                                data-tip="Copied to Clipboard"
                                data-for="Asset_details"
                                data-place="left"
                            />
                        ) : (
                            <i
                                className="fa fa-copy"
                                data-tip="Click to Copy Value"
                                data-for="Asset_details"
                                data-place="left"
                                style={{ color: "#4B90FA" }}
                            />
                        )}
                    </div>
                </CopyToClipboard>
            )
        ) : null;

    const hasAnyValue = Object.entries({
        ...(mainFields ?? {}),
        ...(restFields ?? {})
    })?.some(([key, item]) => {
        if (key === "useful_service_life" || key === "installed_year") return false;

        const value = item?.value;
        return value !== null && value !== undefined && (typeof value !== "string" || value?.trim() !== "");
    });

    const onSaveAll = e => {
        const allFieldValues = {
            ...(mainFields ?? {}),
            ...(restFields ?? {})
        };
        const payload = Object.keys(allFieldValues || {}).reduce((acc, key) => {
            const value = allFieldValues?.[key]?.value;
            if (value !== "" && value !== null && value !== undefined && key !== "Useful_Service_Life" && key !== "installed_year") {
                acc[key] = value;
            }
            return acc;
        }, {});

        onAssetValueSave(payload, false, true);
    };
    const getAssetValue = mapKey => {
        const entry = Object.values(assetValue || {}).find(item => item?.map_key === mapKey);
        return entry?.value ? removeAllTags(entry?.value?.toString()) : "-";
    };

    return (
        <>
            <ReactTooltip id="Asset_details" effect="solid" className="rc-tooltip-custom-class" backgroundColor="#007bff" html />

            <div className="cnt-sec" ref={bandRef}>
                <div className="card ml-0">
                    <div class="card-header" id={keys ? `heading-${keys}-${index + 1}` : `heading-${index + 1}`}>
                        <div className="outer-rcm recommendations otr-recom-div">
                            <button
                                className="btn btn-link"
                                aria-expanded={index === 100 || isOpen ? "true" : "false"}
                                aria-controls={keys ? `collapse-${keys}-${index + 1}` : `collapse-${index + 1}`}
                                onClick={e => {
                                    if (isAiAnalyse) toggleAccordion();
                                }}
                            >
                                {band}
                                {hasAssetValue && (
                                    <span
                                        className={`btn ml-2 filter-button blue-common-btn asset-save-btn pl-3 pr-3 ${
                                            hasAnyValue && !saveDisabledReason ? "" : "cursor-disabled"
                                        }`}
                                        type="button"
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (hasAnyValue && !saveDisabledReason) onSaveAll(e);
                                        }}
                                        data-tip={
                                            saveDisabledReason
                                                ? saveDisabledReason
                                                : hasAnyValue
                                                ? `Save all AI Analyzed field value to ${isMobileRecommendation ? "Recommendation" : " Asset"}`
                                                : "No AI value"
                                        }
                                        data-for="Asset_details"
                                        data-place="left"
                                    >
                                        <img src="/img/Green 1.svg" alt="" /> <span aria-hidden="true">Save All</span>
                                    </span>
                                )}
                                {isAiAnalyse &&
                                    (isOpen ? (
                                        <img src="/img/inner-acco-up-arrow.svg" alt="ss" />
                                    ) : (
                                        <img src="/img/inner-acco-down-arrow.svg" alt="" />
                                    ))}
                            </button>
                            {Object.keys(mainFields).map((eachField, idx) => (
                                <div
                                    className={`${isInaccessible ? "txt-rcm mob-asset" : "txt-rcm"}  ${
                                        (eachField === "Recommendation" || eachField === "Narrative") && isAiAnalyse
                                            ? `analyse-full-width-col ${idx !== mainFields?.length - 1 ? "analyse-full-width-col-border" : ""}`
                                            : ""
                                    }`}
                                >
                                    <div className="txt-dtl content-inp-card">
                                        <div className={`${isAiAnalyse ? "d-flex align-items-center justify-content-between w-100 pr-3" : ""}`}>
                                            <h4 className="mb-0">{mainFields?.[eachField]?.label}</h4>
                                            {isAiAnalyse &&
                                            !Array.isArray(mainFields?.[eachField]?.value) &&
                                            !hasReferenceFieldValues(mainFields?.[eachField]?.value) ? (
                                                !mainFields?.[eachField]?.value ? (
                                                    <div className="icon ai-copy cursor-diabled">
                                                        <i
                                                            className="fa fa-copy"
                                                            data-tip="No value to copy"
                                                            data-for="Asset_details"
                                                            data-place="left"
                                                            style={{ color: "#4B90FA" }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <CopyToClipboard
                                                        text={mainFields?.[eachField]?.value
                                                            ?.toString()
                                                            ?.replace(/\s*\(\s*(?:image|web)(?:\s*\|\s*(?:image|web))?\s*\)/gi, "")}
                                                        onCopy={() => handleCopy(mainFields?.[eachField]?.label)}
                                                    >
                                                        <div className="icon cursor-hand ai-copy">
                                                            {copySuccess === mainFields?.[eachField]?.label ? (
                                                                <i
                                                                    className="fa fa-check"
                                                                    style={{ color: "green" }}
                                                                    data-tip="Copied to Clipboard"
                                                                    data-for="Asset_details"
                                                                    data-place="left"
                                                                />
                                                            ) : (
                                                                <i
                                                                    className="fa fa-copy"
                                                                    data-tip="Click to Copy Value"
                                                                    data-for="Asset_details"
                                                                    data-place="left"
                                                                    style={{ color: "#4B90FA" }}
                                                                />
                                                            )}
                                                        </div>
                                                    </CopyToClipboard>
                                                )
                                            ) : null}
                                        </div>
                                        {hasReferenceFieldValues(mainFields?.[eachField]?.value) && isAiAnalyse ? (
                                            <div className={`d-flex align-items-center pr-3 ${hasAssetValue ? "mt-1 mb-1" : ""}`}>
                                                {hasAssetValue && <h4 className="mb-0 ai-asset-label mr-2">AI Value:&nbsp;</h4>}
                                                <div
                                                    className="custom-selecbox select-multi-box view-user"
                                                    onClick={() =>
                                                        onReferenceFieldClick(
                                                            getReferenceFieldValues(mainFields?.[eachField]?.value),
                                                            eachField,
                                                            {
                                                                useReferenceTable: shouldUseReferenceTable(mainFields?.[eachField]?.value),
                                                                showSaveAction: hasAssetValue,
                                                                saveFieldName: eachField,
                                                                saveDisabledReason: getFieldSaveDisabledReason(eachField)
                                                            }
                                                        )
                                                    }
                                                >
                                                    <div className="badge-num">{getReferenceFieldCount(mainFields?.[eachField]?.value)}</div>
                                                    <div className="badge-sub-txt mr-4">View Values</div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="d-flex">
                                                {hasAssetValue && <h4 className="mb-0 pt-1 ai-asset-label">AI Value:&nbsp;</h4>}
                                                {isEllipsisAiValue(eachField) ? (
                                                    <EllipsisTooltipText
                                                        asHeading
                                                        className="ai-value-ellipsis"
                                                        text={removeAllTags(mainFields?.[eachField]?.value?.toString() ?? "") || "-"}
                                                        tooltipId="Asset_details"
                                                        tooltipPlace="left"
                                                    />
                                                ) : (
                                                <h3
                                                    data-tip={
                                                        !((eachField === "Recommendation" || eachField === "Narrative") && isAiAnalyse)
                                                            ? mainFields?.[eachField]?.value
                                                                ? mainFields?.[eachField]?.value?.toString()?.length >
                                                                  (isAiAnalyse && (eachField === "Recommendation" || eachField === "Narrative")
                                                                      ? 300
                                                                      : isAiAnalyse && !hasAssetValue
                                                                      ? 30
                                                                      : 20)
                                                                    ? mainFields?.[eachField]?.value?.toString()
                                                                    : ""
                                                                : ""
                                                            : ""
                                                    }
                                                    data-multiline={true}
                                                    data-place="left"
                                                    data-for="Asset_details"
                                                    data-effect="solid"
                                                    data-background-color="#4991ff"
                                                >
                                                    {mainFields?.[eachField]?.value
                                                        ? mainFields?.[eachField]?.value?.toString().length >
                                                          (isAiAnalyse && (eachField === "Recommendation" || eachField === "Narrative")
                                                              ? 300
                                                              : isAiAnalyse && !hasAssetValue
                                                              ? 30
                                                              : 20)
                                                            ? removeAllTags(mainFields?.[eachField]?.value?.toString()).substring(
                                                                  0,
                                                                  isAiAnalyse && (eachField === "Recommendation" || eachField === "Narrative")
                                                                      ? 300
                                                                      : isAiAnalyse && !hasAssetValue
                                                                      ? 30
                                                                      : 20
                                                              ) + "..."
                                                            : removeAllTags(mainFields?.[eachField]?.value?.toString()) || "-"
                                                        : "-"}
                                                </h3>
                                                )}
                                                {hasAssetValue &&
                                                    eachField !== "Useful_Service_Life" &&
                                                    eachField !== "installed_year" &&
                                                    eachField !== "Trade" &&
                                                    eachField !== "System" &&
                                                    eachField !== "Sub_System" && (
                                                        <img
                                                            className={`original-asset-value-save ml-2 cursor-hand ${
                                                                mainFields?.[eachField]?.value && !saveDisabledReason ? "" : "cursor-disabled"
                                                            }`}
                                                            src="/img/Green 1.svg"
                                                            alt=""
                                                            data-for="Asset_details"
                                                            data-place="left"
                                                            data-tip={
                                                                saveDisabledReason || (mainFields?.[eachField]?.value ? "Save" : "No value")
                                                            }
                                                            onClick={() => {
                                                                if (mainFields?.[eachField]?.value && !saveDisabledReason)
                                                                    onAssetValueSave({
                                                                        [eachField]: mainFields?.[eachField]?.value
                                                                    });
                                                            }}
                                                        />
                                                    )}
                                                {hasAssetValue &&
                                                    eachField !== "Useful_Service_Life" &&
                                                    eachField !== "installed_year" &&
                                                    eachField !== "Trade" &&
                                                    eachField !== "System" &&
                                                    eachField !== "Sub_System" && (
                                                        <i
                                                            className={`fa fa-pencil-alt ml-2 ai-edit-icon ${
                                                                saveDisabledReason ? "cursor-disabled" : "cursor-hand"
                                                            }`}
                                                            style={{ color: "#4B90FA" }}
                                                            data-for="Asset_details"
                                                            data-place="left"
                                                            data-tip={saveDisabledReason || "Edit"}
                                                            onClick={() => {
                                                                if (!saveDisabledReason && typeof onEditValueClick === "function")
                                                                    onEditValueClick(
                                                                        eachField,
                                                                        mainFields?.[eachField]?.value,
                                                                        mainFields?.[eachField]?.label
                                                                    );
                                                            }}
                                                        />
                                                    )}
                                            </div>
                                        )}
                                        {hasAssetValue && (
                                            <span className="original-asset-value">
                                                <span className="d-flex">
                                                    <h4 className="mb-0 pt-1 ai-asset-label">
                                                        {isMobileRecommendation ? "Recom." : "Asset"} Value:&nbsp;
                                                    </h4>
                                                    <EllipsisTooltipText
                                                        asHeading
                                                        className="ai-value-ellipsis mt-0"
                                                        text={getAssetValue(eachField)}
                                                        tooltipId="Asset_details"
                                                        tooltipPlace="left"
                                                    />
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div
                        id={keys ? `collapse-${keys}-${index + 1}` : `collapse-${index + 1}`}
                        class={index === 100 || isOpen ? "collapse show" : "collapse"}
                        aria-labelledby={keys ? `heading-${keys}-${index + 1}` : `heading-${index + 1}`}
                    >
                        <div class="card-body">
                            {Array.from({ length: outerDivCount }).map((_, index) => {
                                const start = index * divCounts;
                                const end = start + divCounts;
                                const chunk = Object.keys(restFields ?? {}).slice(start, end);
                                return (
                                    <div key={index + 1} className="outer-rcm recommendations">
                                        {chunk.map((eachRestField, idx) => (
                                            <div
                                                className={`${bandKey === "survey_details" ? "txt-rcm mob-asset" : "txt-rcm"} ${
                                                    (eachRestField === "Recommendation" || eachRestField === "Narrative") && isAiAnalyse
                                                        ? `analyse-full-width-col ${idx !== chunk?.length - 1 ? "analyse-full-width-col-border" : ""}`
                                                        : ""
                                                }`}
                                            >
                                                <div className="txt-dtl content-inp-card">
                                                    {eachRestField === "Narrative" && isAiAnalyse ? (
                                                        <div class="expand-window-icon ckeditor-band">
                                                            <h4>{restFields?.[eachRestField]?.label}</h4>
                                                            {isAiAnalyse ? (
                                                                <div className="d-flex mr-3">
                                                                    {copyFunction(eachRestField)}
                                                                    <span
                                                                        class="ml-1"
                                                                        data-for="input_field"
                                                                        data-tip="Expand Field"
                                                                        onClick={e => {
                                                                            e.preventDefault();
                                                                            setShowRichTextPopup({
                                                                                show: true,
                                                                                label: restFields?.[eachRestField]?.label,
                                                                                value: restFields?.[eachRestField]?.value,
                                                                                key: restFields?.[eachRestField]?.field_name
                                                                            });
                                                                        }}
                                                                    >
                                                                        <img src="/img/expand1.svg" alt="" />
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span
                                                                    data-for="input_field"
                                                                    data-tip="Expand Field"
                                                                    onClick={e => {
                                                                        e.preventDefault();
                                                                        setShowRichTextPopup({
                                                                            show: true,
                                                                            label: restFields?.[eachRestField]?.label,
                                                                            value: restFields?.[eachRestField]?.value,
                                                                            key: restFields?.[eachRestField]?.field_name
                                                                        });
                                                                    }}
                                                                >
                                                                    <img src="/img/expand1.svg" alt="" />
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={`${
                                                                isAiAnalyse ? "d-flex align-items-center justify-content-between w-100 pr-3" : ""
                                                            }`}
                                                        >
                                                            <h4 className="mb-0">{restFields?.[eachRestField]?.label}</h4>
                                                            {isAiAnalyse ? copyFunction(eachRestField) : null}
                                                        </div>
                                                    )}

                                                    {hasReferenceFieldValues(restFields?.[eachRestField]?.value) && isAiAnalyse ? (
                                                        <div className={`d-flex align-items-center pr-3 ${hasAssetValue ? "mt-1 mb-1" : ""}`}>
                                                            {hasAssetValue && <h4 className="mb-0 ai-asset-label mr-2">AI Value:&nbsp;</h4>}
                                                            <div
                                                                className="custom-selecbox select-multi-box view-user"
                                                                onClick={() =>
                                                                    onReferenceFieldClick(
                                                                        getReferenceFieldValues(restFields?.[eachRestField]?.value),
                                                                        eachRestField,
                                                                        {
                                                                            useReferenceTable: shouldUseReferenceTable(
                                                                                restFields?.[eachRestField]?.value
                                                                            ),
                                                                            showSaveAction: hasAssetValue,
                                                                            saveFieldName: eachRestField,
                                                                            saveDisabledReason: getFieldSaveDisabledReason(eachRestField)
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                <div className="badge-num">
                                                                    {getReferenceFieldCount(restFields?.[eachRestField]?.value)}
                                                                </div>
                                                                <div className="badge-sub-txt mr-4">View Values</div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className={`d-flex ${hasAssetValue ? "mt-1" : ""}`}>
                                                            {hasAssetValue && <h4 className="mb-0 pt-1 ai-asset-label">AI Value:&nbsp;</h4>}
                                                            {isEllipsisAiValue(eachRestField) &&
                                                            !Array.isArray(restFields?.[eachRestField]?.value) ? (
                                                                <EllipsisTooltipText
                                                                    asHeading
                                                                    className="ai-value-ellipsis mt-0"
                                                                    text={removeAllTags(restFields?.[eachRestField]?.value?.toString() ?? "") || "-"}
                                                                    tooltipId="Asset_details"
                                                                    tooltipPlace="left"
                                                                />
                                                            ) : (
                                                            <h3
                                                                className={hasAssetValue ? "mt-0" : ""}
                                                                data-tip={
                                                                    !(Array.isArray(restFields?.[eachRestField]?.value) && isAiAnalyse) &&
                                                                    !(
                                                                        (eachRestField === "Recommendation" || eachRestField === "Narrative") &&
                                                                        isAiAnalyse
                                                                    )
                                                                        ? restFields?.[eachRestField]?.value
                                                                            ? restFields?.[eachRestField]?.value?.toString()?.length >
                                                                              (isAiAnalyse &&
                                                                              (eachRestField === "Recommendation" || eachRestField === "Narrative")
                                                                                  ? 300
                                                                                  : isAiAnalyse && !hasAssetValue
                                                                                  ? 30
                                                                                  : 20)
                                                                                ? restFields?.[eachRestField]?.value?.toString()
                                                                                : ""
                                                                            : ""
                                                                        : ""
                                                                }
                                                                data-multiline={true}
                                                                data-place="left"
                                                                data-for="Asset_details"
                                                                data-effect="solid"
                                                                data-background-color="#4991ff"
                                                            >
                                                                {restFields?.[eachRestField]?.value
                                                                    ? restFields?.[eachRestField]?.value?.toString().length >
                                                                      (isAiAnalyse &&
                                                                      (eachRestField === "Recommendation" || eachRestField === "Narrative")
                                                                          ? 300
                                                                          : isAiAnalyse && !hasAssetValue
                                                                          ? 30
                                                                          : 20)
                                                                        ? removeAllTags(restFields?.[eachRestField]?.value?.toString()).substring(
                                                                              0,
                                                                              isAiAnalyse &&
                                                                                  (eachRestField === "Recommendation" ||
                                                                                      eachRestField === "Narrative")
                                                                                  ? 300
                                                                                  : isAiAnalyse && !hasAssetValue
                                                                                  ? 30
                                                                                  : 20
                                                                          ) + "..."
                                                                        : removeAllTags(restFields?.[eachRestField]?.value?.toString()) || "-"
                                                                    : "-"}
                                                            </h3>
                                                            )}
                                                            {hasAssetValue &&
                                                                eachRestField !== "Useful_Service_Life" &&
                                                                eachRestField !== "installed_year" &&
                                                                eachRestField !== "Trade" &&
                                                                eachRestField !== "System" &&
                                                                eachRestField !== "Sub_System" && (
                                                                    <img
                                                                        className={`original-asset-value-save ml-2 cursor-hand ${
                                                                            hasAssetValue ? "mt-0" : "mt-3"
                                                                        } ${
                                                                            restFields?.[eachRestField]?.value && !saveDisabledReason
                                                                                ? ""
                                                                                : "cursor-disabled"
                                                                        }`}
                                                                        src="/img/Green 1.svg"
                                                                        alt=""
                                                                        data-for="Asset_details"
                                                                        data-place="left"
                                                                        data-tip={
                                                                            saveDisabledReason ||
                                                                            (restFields?.[eachRestField]?.value ? "Save" : "No value")
                                                                        }
                                                                        onClick={() => {
                                                                            if (restFields?.[eachRestField]?.value && !saveDisabledReason)
                                                                                onAssetValueSave({
                                                                                    [eachRestField]: restFields?.[eachRestField]?.value
                                                                                });
                                                                        }}
                                                                    />
                                                                )}
                                                            {hasAssetValue &&
                                                                eachRestField !== "Useful_Service_Life" &&
                                                                eachRestField !== "installed_year" &&
                                                                eachRestField !== "Trade" &&
                                                                eachRestField !== "System" &&
                                                                eachRestField !== "Sub_System" && (
                                                                    <i
                                                                        className={`fa fa-pencil-alt ml-2 ai-edit-icon ${
                                                                            saveDisabledReason ? "cursor-disabled" : "cursor-hand"
                                                                        }`}
                                                                        style={{ color: "#4B90FA" }}
                                                                        data-for="Asset_details"
                                                                        data-place="left"
                                                                        data-tip={saveDisabledReason || "Edit"}
                                                                        onClick={() => {
                                                                            if (!saveDisabledReason && typeof onEditValueClick === "function")
                                                                                onEditValueClick(
                                                                                    eachRestField,
                                                                                    restFields?.[eachRestField]?.value,
                                                                                    restFields?.[eachRestField]?.label
                                                                                );
                                                                        }}
                                                                    />
                                                                )}
                                                        </div>
                                                    )}
                                                    {hasAssetValue && (
                                                        <span className="original-asset-value">
                                                            <span className="d-flex">
                                                                <h4 className="mb-0 pt-1 ai-asset-label">
                                                                    {isMobileRecommendation ? "Recom." : "Asset"} Value:&nbsp;
                                                                </h4>
                                                                <EllipsisTooltipText
                                                                    asHeading
                                                                    className="ai-value-ellipsis mt-0"
                                                                    text={getAssetValue(eachRestField)}
                                                                    tooltipId="Asset_details"
                                                                    tooltipPlace="left"
                                                                />
                                                            </span>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AnalyseBand;
